import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import { usdcToken } from '@shared/data/token'
import { getIndexerOracleApi } from '@shared/service/oracle'
import { getIndexerGrpcWsPriceOracleApi } from '@/app/Services'
import { WS_PRICE_ORACLE_API_KEY } from '@/app/utils/constants'
import {
  getScaledMarketDisplayPrice,
  derivativeMarketsToOraclePriceV2Filters
} from '@/app/utils/marketPrice'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import type { BffDerivativeMarket } from '@shared/types'
import type { TcDerivativePosition } from '@injectivelabs/sdk-ts'
import type {
  OraclePriceV2,
  WsPriceOracleMarketPrice
} from '@injectivelabs/sdk-ts/client/indexer'
import type {
  MarketMarkPriceMap,
  MarketIndexPriceMap,
  MarketDisplayPriceMap
} from '@/types/trade'

type DerivativePricesStoreState = {
  marketMarkPriceMap: MarketMarkPriceMap
  marketIndexPriceMap: MarketIndexPriceMap
  marketMarkDisplayPriceMap: MarketDisplayPriceMap
  marketIndexDisplayPriceMap: MarketDisplayPriceMap
}

const MARKET_PRICE_SNAPSHOT_CHUNK_SIZE = 50

const initialStateFactory = (): DerivativePricesStoreState => ({
  marketMarkPriceMap: {} as MarketMarkPriceMap,
  marketIndexPriceMap: {} as MarketIndexPriceMap,
  marketMarkDisplayPriceMap: {} as MarketDisplayPriceMap,
  marketIndexDisplayPriceMap: {} as MarketDisplayPriceMap
})

export const useDerivativePricesStore = defineStore('derivativePrices', {
  state: (): DerivativePricesStoreState => initialStateFactory(),
  actions: {
    setMarketIndexPrice(
      marketId: string,
      marketIndexPrice: MarketIndexPriceMap[string]
    ) {
      this.mergeMarketIndexPriceMap({
        [marketId]: marketIndexPrice
      })
    },

    setMarketIndexPriceMap(
      marketIndexPriceMap: MarketIndexPriceMap,
      markets: BffDerivativeMarket[] = []
    ) {
      replaceMarketPriceMap({
        markets,
        priceMap: this.marketIndexPriceMap,
        nextPriceMap: marketIndexPriceMap,
        displayPriceMap: this.marketIndexDisplayPriceMap
      })
    },

    mergeMarketIndexPriceMap(
      marketIndexPriceMap: MarketIndexPriceMap,
      markets: BffDerivativeMarket[] = []
    ) {
      mergeMarketPriceMap({
        markets,
        priceMap: this.marketIndexPriceMap,
        incomingPriceMap: marketIndexPriceMap,
        displayPriceMap: this.marketIndexDisplayPriceMap
      })
    },

    deleteMarketIndexPrices(marketIds: string[]) {
      deleteMarketPrices({
        marketIds,
        priceMap: this.marketIndexPriceMap,
        displayPriceMap: this.marketIndexDisplayPriceMap
      })
    },

    setMarketMarkPrice(
      marketId: string,
      marketMarkPrice: MarketMarkPriceMap[string]
    ) {
      this.mergeMarketMarkPriceMap({
        [marketId]: marketMarkPrice
      })
    },

    setMarketMarkPriceMap(
      marketMarkPriceMap: MarketMarkPriceMap,
      markets: BffDerivativeMarket[] = []
    ) {
      replaceMarketPriceMap({
        markets,
        priceMap: this.marketMarkPriceMap,
        nextPriceMap: marketMarkPriceMap,
        displayPriceMap: this.marketMarkDisplayPriceMap
      })
    },

    mergeMarketMarkPriceMap(
      marketMarkPriceMap: MarketMarkPriceMap,
      markets: BffDerivativeMarket[] = []
    ) {
      mergeMarketPriceMap({
        markets,
        priceMap: this.marketMarkPriceMap,
        incomingPriceMap: marketMarkPriceMap,
        displayPriceMap: this.marketMarkDisplayPriceMap
      })
    },

    deleteMarketMarkPrices(marketIds: string[]) {
      deleteMarketPrices({
        marketIds,
        priceMap: this.marketMarkPriceMap,
        displayPriceMap: this.marketMarkDisplayPriceMap
      })
    },

    async fetchMarketsIndexPrices({
      markets = [],
      marketIds
    }: {
      marketIds: string[]
      markets?: BffDerivativeMarket[]
    }) {
      const marketIdsToFetch = uniqueMarketIds(marketIds)

      if (marketIdsToFetch.length === 0) {
        return
      }

      if (!WS_PRICE_ORACLE_API_KEY) {
        this.deleteMarketIndexPrices(marketIdsToFetch)

        return
      }

      const indexerGrpcWsPriceOracleApi = await getIndexerGrpcWsPriceOracleApi()

      if (!indexerGrpcWsPriceOracleApi) {
        return
      }

      const prices: WsPriceOracleMarketPrice[] = []

      for (const marketIdsChunk of chunkArray(
        marketIdsToFetch,
        MARKET_PRICE_SNAPSHOT_CHUNK_SIZE
      )) {
        const response =
          await indexerGrpcWsPriceOracleApi.fetchLatestMarketPrices({
            marketIds: marketIdsChunk
          })

        prices.push(...response.prices)
      }

      const marketIndexPriceMap = prices.reduce(
        (priceMap, marketIndexPrice) => {
          if (!marketIndexPrice.marketId || !marketIndexPrice.price) {
            return priceMap
          }

          priceMap[marketIndexPrice.marketId] = markRaw({
            marketId: marketIndexPrice.marketId,
            price: marketIndexPrice.price,
            ...(marketIndexPrice.timestamp && {
              timestamp: Number(marketIndexPrice.timestamp)
            })
          })

          return priceMap
        },
        {} as MarketIndexPriceMap
      )

      this.deleteMarketIndexPrices(
        marketIdsToFetch.filter((marketId) => !marketIndexPriceMap[marketId])
      )
      this.mergeMarketIndexPriceMap(marketIndexPriceMap, markets)
    },

    async fetchMarketsMarkPrices({
      markets
    }: {
      markets: BffDerivativeMarket[]
    }) {
      const marketIdsToFetch = uniqueMarketIds(
        markets.map((market) => market.marketId)
      )

      if (marketIdsToFetch.length === 0) {
        return
      }

      const filters = derivativeMarketsToOraclePriceV2Filters(markets)

      if (filters.length === 0) {
        this.deleteMarketMarkPrices(marketIdsToFetch)

        return
      }

      const indexerOracleApi = await getIndexerOracleApi()
      const marketMarkPrices: OraclePriceV2[] = []

      for (const filtersChunk of chunkArray(
        filters,
        MARKET_PRICE_SNAPSHOT_CHUNK_SIZE
      )) {
        marketMarkPrices.push(
          ...(await indexerOracleApi.fetchOraclePriceV2(filtersChunk))
        )
      }

      const marketMarkPriceMap = marketMarkPrices.reduce(
        (priceMap, marketMarkPrice) => {
          if (!marketMarkPrice.marketId || !marketMarkPrice.price) {
            return priceMap
          }

          priceMap[marketMarkPrice.marketId] = markRaw({
            marketId: marketMarkPrice.marketId,
            price: marketMarkPrice.price
          })

          return priceMap
        },
        {} as MarketMarkPriceMap
      )

      this.deleteMarketMarkPrices(
        marketIdsToFetch.filter((marketId) => !marketMarkPriceMap[marketId])
      )
      this.mergeMarketMarkPriceMap(marketMarkPriceMap, markets)
    },

    async getMarketMarkPrice(market: BffDerivativeMarket) {
      const derivativePricesStore = useDerivativePricesStore()
      const indexerOracleApi = await getIndexerOracleApi()

      const oraclePrice = await apiCallsRequestCache.dedupe(
        createRequestKey(CLIENT_REQUEST_KEYS.marketMarkPrice, {
          marketId: market.marketId,
          oracleType: market.oracleType,
          baseSymbol: market.oracleBase,
          quoteSymbol: market.oracleQuote
        }),
        () =>
          indexerOracleApi.fetchOraclePrice({
            oracleType: market.oracleType,
            baseSymbol: market.oracleBase,
            quoteSymbol: market.oracleQuote
          })
      )

      derivativePricesStore.setMarketMarkPrice(market.marketId, {
        marketId: market.marketId,
        price: oraclePrice.price
      })
    },

    updateMarkPriceMapFromPosition(positions: TcDerivativePosition[]) {
      const derivativePricesStore = useDerivativePricesStore()
      const marketStore = useDerivativeMarketStore()

      const markPricesMap = positions.reduce((markPrices, position) => {
        const market = marketStore.marketByIdOrSlug(position.marketId)

        return {
          ...markPrices,
          [position.marketId]: {
            marketId: position.marketId,
            price: toHumanReadable(
              position.markPrice,
              market?.quoteToken.decimals || usdcToken.decimals
            ).toFixed()
          }
        }
      }, {} as MarketMarkPriceMap)

      derivativePricesStore.mergeMarketMarkPriceMap(markPricesMap)
    },

    reset() {
      const derivativePricesStore = useDerivativePricesStore()

      derivativePricesStore.setMarketMarkPriceMap({})
      derivativePricesStore.setMarketIndexPriceMap({})
    }
  }
})

function uniqueMarketIds(marketIds: string[]) {
  return [...new Set(marketIds.filter(Boolean))]
}

function chunkArray<Value>(values: Value[], chunkSize: number) {
  const chunks: Value[][] = []

  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize))
  }

  return chunks
}

function replaceMarketPriceMap<
  MarketPriceMap extends MarketMarkPriceMap | MarketIndexPriceMap
>({
  priceMap,
  nextPriceMap,
  markets,
  displayPriceMap
}: {
  priceMap: MarketPriceMap
  nextPriceMap: MarketPriceMap
  markets: BffDerivativeMarket[]
  displayPriceMap: MarketDisplayPriceMap
}) {
  for (const marketId of Object.keys(priceMap)) {
    if (nextPriceMap[marketId]) {
      continue
    }

    delete priceMap[marketId]
    delete displayPriceMap[marketId]
  }

  for (const marketId of Object.keys(displayPriceMap)) {
    if (nextPriceMap[marketId]) {
      continue
    }

    delete displayPriceMap[marketId]
  }

  mergeRawMarketPriceMap(priceMap, nextPriceMap)
  syncDisplayPriceMap({ markets, priceMap, displayPriceMap })
}

function mergeMarketPriceMap<
  MarketPriceMap extends MarketMarkPriceMap | MarketIndexPriceMap
>({
  priceMap,
  markets,
  displayPriceMap,
  incomingPriceMap
}: {
  priceMap: MarketPriceMap
  markets: BffDerivativeMarket[]
  incomingPriceMap: MarketPriceMap
  displayPriceMap: MarketDisplayPriceMap
}) {
  mergeRawMarketPriceMap(priceMap, incomingPriceMap)
  syncDisplayPriceMap({
    priceMap,
    markets,
    displayPriceMap,
    marketIds: Object.keys(incomingPriceMap)
  })
}

function mergeRawMarketPriceMap<
  MarketPriceMap extends MarketMarkPriceMap | MarketIndexPriceMap
>(priceMap: MarketPriceMap, incomingPriceMap: MarketPriceMap) {
  for (const [marketId, marketPrice] of Object.entries(incomingPriceMap)) {
    const existingPrice = priceMap[marketId]

    if (
      existingPrice?.price === marketPrice.price &&
      existingPrice?.timestamp === marketPrice.timestamp
    ) {
      continue
    }

    priceMap[marketId] = markRaw(marketPrice)
  }
}

function deleteMarketPrices<
  MarketPriceMap extends MarketMarkPriceMap | MarketIndexPriceMap
>({
  marketIds,
  priceMap,
  displayPriceMap
}: {
  marketIds: string[]
  priceMap: MarketPriceMap
  displayPriceMap: MarketDisplayPriceMap
}) {
  for (const marketId of marketIds) {
    delete priceMap[marketId]
    delete displayPriceMap[marketId]
  }
}

function syncDisplayPriceMap<
  MarketPriceMap extends MarketMarkPriceMap | MarketIndexPriceMap
>({
  priceMap,
  markets,
  marketIds,
  displayPriceMap
}: {
  marketIds?: string[]
  priceMap: MarketPriceMap
  markets: BffDerivativeMarket[]
  displayPriceMap: MarketDisplayPriceMap
}) {
  if (markets.length === 0) {
    return
  }

  const marketById = createMarketByIdMap(markets)
  const marketIdsToSync = marketIds ?? Object.keys(priceMap)

  for (const marketId of marketIdsToSync) {
    const market = marketById.get(marketId)
    const marketPrice = priceMap[marketId]

    if (!market || !marketPrice) {
      continue
    }

    const displayPrice = getScaledMarketDisplayPrice({
      market,
      price: marketPrice.price
    })

    if (!displayPrice) {
      delete displayPriceMap[marketId]

      continue
    }

    if (displayPriceMap[marketId] === displayPrice) {
      continue
    }

    displayPriceMap[marketId] = displayPrice
  }
}

function createMarketByIdMap(markets: BffDerivativeMarket[]) {
  return new Map(markets.map((market) => [market.marketId, market]))
}
